import { useContext } from 'react'
import { State } from './context'
import { parseQuery } from './query'

export const PAGE_SIZE = 50

const HISTORY_SIZE = 5
const TESTAMENTS = {ot: [1, 39], nt: [40, 66]}

let worker
let request = 0
let latest = 0
const pending = new Map()

const getWorker = () => {
  if (!worker) {
    worker = new Worker('/assets/worker.js', {type: 'module'})

    worker.addEventListener('message', ({data}) => {
      const callbacks = pending.get(data.id)

      if (!callbacks) return
      if (data.progress != null) return callbacks.onProgress(data.progress)

      pending.delete(data.id)
      data.error ? callbacks.reject(new Error('Search failed')) : callbacks.resolve(data.results)
    })

    worker.addEventListener('error', () => {
      pending.forEach(callbacks => callbacks.reject(new Error('Worker failed')))
      pending.clear()
      worker = null
    })
  }

  return worker
}

// Wyszukiwanie działa w osobnym wątku, żeby przeszukiwanie całej Biblii nie blokowało interfejsu.
const findVerses = (bible, text, onProgress) => new Promise((resolve, reject) => {
  const id = ++request

  pending.set(id, {resolve, reject, onProgress})
  getWorker().postMessage({id, bible, text})
})

export const resultKey = ({book, chapter, verse}) => `${book}.${chapter}.${verse}`

export const inFilter = (filter, book) => (
  !filter
    ? true
    : TESTAMENTS[filter]
      ? book >= TESTAMENTS[filter][0] && book <= TESTAMENTS[filter][1]
      : book == filter
)

export const countLetters = text => text.match(/\p{L}/gu)?.length ?? 0

const remember = (history, query) => [query, ...history.filter(item => item != query)].slice(0, HISTORY_SIZE)

export const useSearch = () => {
  const [{input}, setState] = useContext(State)

  // Otwiera rozdział z odnośnika lub wyniku; zapytanie trafia do ostatnich wyszukiwań.
  // Po otwarciu wyniku (`back`) rozdział pokazuje przycisk powrotu do listy.
  const navigate = (reference, query, {clear = false, back = false} = {}) => setState(state => ({
    ...state,
    input: {
      ...state.input,
      book: reference.book,
      chapter: reference.chapter
    },
    target: reference.verses,
    search: {
      ...state.search,
      input: clear ? '' : state.search.input,
      back,
      history: query?.trim() ? remember(state.search.history, query.trim()) : state.search.history
    },
    active: {
      ...state.active,
      search: false,
      input: false,
      verses: {},
      control: {}
    }
  }))

  const find = async (query, {bible = input.bible, save = false} = {}) => {
    const {text} = parseQuery(query, input.book)
    const id = ++latest

    if (!text) return setState(state => ({
      ...state,
      search: {
        ...state.search,
        query: '',
        terms: [],
        results: [],
        status: 'idle'
      }
    }))

    scrollTo({top: 0})

    // Poprzednie wyniki zostają widoczne do czasu nadejścia nowych, żeby lista nie migała podczas pisania.
    setState(state => ({
      ...state,
      active: {
        ...state.active,
        verses: {}
      },
      search: {
        ...state.search,
        query,
        bible,
        status: 'loading',
        progress: 0,
        filter: null,
        shown: PAGE_SIZE,
        scroll: 0,
        history: save ? remember(state.search.history, query.trim()) : state.search.history
      }
    }))

    const onProgress = progress => id == latest && setState(state => ({
      ...state,
      search: {
        ...state.search,
        progress
      }
    }))

    try {
      const results = await findVerses(bible, text, onProgress)

      id == latest && setState(state => ({
        ...state,
        search: {
          ...state.search,
          results,
          terms: text.include,
          status: 'done'
        }
      }))
    }
    catch {
      id == latest && setState(state => ({
        ...state,
        search: {
          ...state.search,
          results: [],
          status: 'error'
        }
      }))
    }
  }

  // Enter: odnośnik (lub pierwsza podpowiedź z numerem rozdziału) otwiera rozdział, inne zapytanie szuka tekstu.
  const submit = query => {
    const {reference, suggestions, text} = parseQuery(query, input.book)
    const target = reference ?? (!text && suggestions[0])

    target ? navigate(target, query, {clear: true}) : find(query, {save: true})
  }

  // Podczas pisania szukamy tekstu dopiero od 3 liter; odnośniki z numerem pokazują tylko podpowiedź.
  const live = query => {
    const {text} = parseQuery(query, input.book)

    text && countLetters(query) >= 3 ? find(query) : find('')
  }

  return {navigate, find, submit, live}
}
